import { useCallback, useMemo, useState, type FC } from "react";
import styled from "styled-components";

import { useRootDispatch } from "@ds/hooks";

import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { InputNumber } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { IconType } from "@ds/components/icons";
import { Dialog } from "@ds/components/modals";

import { campaignItemsActions } from "../../redux/slice";

const ContentWrapper = styled.div`
  min-height: 80px;
  width: 360px;
`;

const FooterWrapper = styled.div`
  display: flex;
  justify-content: right;
`;

export const BulkWeightModal: FC<{
  allItems: CampaignItem[];
  selectedIds: number[];
  onClose: () => void;
}> = ({ allItems, selectedIds, onClose }) => {
  const unselectedCount = allItems.length - selectedIds.length;
  // Each selected item gets `weight`; each unselected item needs at least 1%.
  // Max per selected item = floor((100 - unselectedCount) / selectedIds.length).
  const maxWeight =
    selectedIds.length === 0 ? 100 : Math.max(1, Math.floor((100 - unselectedCount) / selectedIds.length));

  const [weight, setWeight] = useState<number>(Math.min(10, maxWeight));

  const dispatch = useRootDispatch();
  const onApplyHandler = useCallback(() => {
    if (selectedIds.length === 0) {
      onClose();
      return;
    }

    const weights = new Map<number, number>();

    // All selected items get the same weight.
    for (const id of selectedIds) {
      weights.set(id, weight);
    }

    // Distribute remainder proportionally across unselected items.
    const unselected = allItems.filter(item => !selectedIds.includes(item.id));
    if (unselected.length > 0) {
      const totalSelectedWeight = weight * selectedIds.length;
      const remainderForUnselected = 100 - totalSelectedWeight;
      const totalUnselectedWeight = unselected.reduce((sum, item) => sum + (item.weight ?? 1), 0);
      let distributed = 0;

      for (const item of unselected) {
        const share = Math.max(1, Math.floor(((item.weight ?? 1) / totalUnselectedWeight) * remainderForUnselected));
        weights.set(item.id, share);
        distributed += share;
      }

      // Assign rounding remainder to items with the highest campaign_order.
      let rem = remainderForUnselected - distributed;
      const sortedUnselected = [...unselected].sort((a, b) => b.campaign_order - a.campaign_order);
      for (const item of sortedUnselected) {
        if (rem <= 0) break;
        weights.set(item.id, (weights.get(item.id) ?? 0) + 1);
        rem--;
      }
    }

    // Normalize to exactly 100 (handles rounding when all items are selected).
    const total = [...weights.values()].reduce((s, w) => s + w, 0);
    const diff = 100 - total;
    if (diff !== 0) {
      const step = diff > 0 ? 1 : -1;
      const sortedAll = [...allItems].sort((a, b) => b.campaign_order - a.campaign_order);
      let remaining = Math.abs(diff);
      while (remaining > 0) {
        let changed = false;
        for (const item of sortedAll) {
          if (remaining === 0) break;
          const next = (weights.get(item.id) ?? 0) + step;
          if (next >= 1) {
            weights.set(item.id, next);
            remaining--;
            changed = true;
          }
        }
        if (!changed) break;
      }
    }

    const updates = allItems
      .filter(item => weights.has(item.id) && weights.get(item.id) !== item.weight)
      .map(item => ({ id: item.id, fields: { weight: weights.get(item.id)! } }));

    if (updates.length > 0) {
      dispatch(campaignItemsActions.batchUpdateCampaignItemWeights(updates));
    }
    onClose();
  }, [dispatch, allItems, selectedIds, weight, onClose]);

  const renderFooter = useMemo(
    () => (
      <FooterWrapper>
        <Button
          label="Cancel"
          ariaLabel="cancel button"
          buttonType={ButtonType.Outlined}
          actionType={ButtonActionType.Secondary}
          icon={IconType.Close}
          iconPosition="right"
          onClick={onClose}
        />
        <Button
          label="Apply"
          ariaLabel="apply button"
          buttonType={ButtonType.Raised}
          icon={IconType.Save}
          iconPosition="right"
          onClick={onApplyHandler}
        />
      </FooterWrapper>
    ),
    [onClose, onApplyHandler],
  );

  return (
    <Dialog
      modal
      visible
      header={`Set weight for ${selectedIds.length} item(s)`}
      footer={renderFooter}
      onHide={onClose}
    >
      <ContentWrapper>
        <LabelWrapper label="Weight (%)" className="col-8" isRequired>
          <InputNumber
            value={weight}
            min={1}
            max={maxWeight}
            suffix="%"
            autoFocus
            onChange={({ value: v }) => setWeight(v ?? 1)}
          />
        </LabelWrapper>
      </ContentWrapper>
    </Dialog>
  );
};
